import { skipToken } from '@reduxjs/toolkit/query';
import { createContext, ReactNode, useContext, useState } from 'react';
import { Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { useGetListViewsQuery } from 'store/api/listViews';
import { isListPage, ListKey, listKeyOfPath, ListView, ListViews } from 'types/listViews';
import {
    ALL_VIEW,
    ListViewsPage,
    ListViewsPageContext,
    SAVED_VIEW_PARAM_KEY,
    viewQueryOf,
    withViewQuery,
} from './builtInViews';

export type ListViewsScopeValue = {
    listKey: ListKey;
    page: ListViewsPage;
    data: ListViews | undefined;
    /** The saved view the URL names, when the viewer can open it. */
    activeView: ListView | undefined;
    /** The list is a record's tab, which keeps its own filter on whatever view is open. */
    inRecord: boolean;
};

export const ListViewsScopeContext = createContext<ListViewsScopeValue | undefined>(undefined);

/** A URL naming neither filters nor sort opens on a view: the one it names, or the viewer's default. */
const opensOnView = (search: string) => viewQueryOf(search) === '';

/** Where a list opened on `search` goes to show its view, or nothing when it already shows it. */
function openingLocation(data: ListViews, page: ListViewsPage, pathname: string, search: string) {
    const named = new URLSearchParams(search).get(SAVED_VIEW_PARAM_KEY);
    const pathOf = (builtInId: string) => page.pathFor?.(builtInId) ?? pathname;
    if (named) {
        const view = data.views.find((candidate) => String(candidate.id) === named);
        return view && (viewQueryOf(view.state.query) || pathOf(view.baseViewKey) !== pathname)
            ? `${pathOf(view.baseViewKey)}${withViewQuery(search, view.id, view.state.query)}`
            : undefined;
    }
    const defaultView = data.views.find((candidate) => candidate.id === data.defaultListViewId);
    if (defaultView) {
        return `${pathOf(defaultView.baseViewKey)}${withViewQuery(search, defaultView.id, defaultView.state.query)}`;
    }
    const builtIn = page.views.find((view) => view.id === data.defaultBuiltInViewKey);
    return builtIn && builtIn.id !== page.activeId
        ? `${pathOf(builtIn.id)}${withViewQuery(search, builtIn.id)}`
        : undefined;
}

/**
 * The saved views of the list the page shows, for a list that owns its URL in the new design. A list opened
 * without filters or sort goes to its view first: the saved view its URL names, or the viewer's default. So
 * does a later link to the bare list, such as the sidebar's; the list's own navigations keep `savedView`. The
 * list mounts once that is settled, so it never fetches what it is about to leave. A list in a record's tab,
 * named by `listKey`, has the same views but never opens on the default. A page path that is no list of
 * views gets no scope.
 */
export default function ListViewsScope({ listKey, children }: { listKey?: ListKey; children: ReactNode }) {
    const providedPage = useContext(ListViewsPageContext);
    const { pathname, search, key } = useLocation();
    const ownKey = listKey ?? listKeyOfPath(pathname);
    const page: ListViewsPage | undefined =
        providedPage ?? (ownKey ? { listKey: ownKey, views: [ALL_VIEW], activeId: ALL_VIEW.id } : undefined);
    const inRecord = !!page && !isListPage(pathname, page.listKey);
    const { data, isError } = useGetListViewsQuery(page?.listKey ?? skipToken);

    // The URL being opened, until its view is decided; any other URL is the user's.
    const namesView = (query: string) => new URLSearchParams(query).has(SAVED_VIEW_PARAM_KEY);
    const [opening, setOpening] = useState(() =>
        opensOnView(search) && (!inRecord || namesView(search)) ? search : null
    );
    const [seenKey, setSeenKey] = useState(key);
    if (key !== seenKey) {
        setSeenKey(key);
        setOpening(!inRecord && opensOnView(search) && !namesView(search) ? search : null);
    }

    if (!page) return children;

    if (opening !== null && !isError) {
        if (!data) return null;
        const target = openingLocation(data, page, pathname, opening);
        if (target) return <Navigate to={target} replace />;
        setOpening(null);
    }

    const named = new URLSearchParams(search).get(SAVED_VIEW_PARAM_KEY);
    const activeView = data?.views.find((view) => String(view.id) === named);
    return (
        <ListViewsScopeContext.Provider value={{ listKey: page.listKey, page, data, activeView, inRecord }}>
            {children}
        </ListViewsScopeContext.Provider>
    );
}

/**
 * The built-in view a list in a record's tab shows, for a list whose built-in views change what it fetches: the
 * one its URL names, or the one the saved view it names is based on, else `fallback`. Undefined until that
 * saved view is known.
 */
export function useRecordTabBuiltIn(listKey: ListKey | undefined, builtInIds: readonly string[], fallback: string) {
    const named = useSearchParams()[0].get(SAVED_VIEW_PARAM_KEY);
    const savedId = named && !builtInIds.includes(named) ? named : null;
    const { data, isFetching, isError } = useGetListViewsQuery(listKey && savedId ? listKey : skipToken);
    const [last, setLast] = useState<string>();
    const baseId = data?.views.find((view) => String(view.id) === savedId)?.baseViewKey;
    // A view just saved here is missing until the list's views refetch, and is based on the view shown.
    const shown =
        !listKey || !named || isError ? fallback : !savedId ? named : (baseId ?? (isFetching ? last : fallback));
    if (shown !== undefined && shown !== last) setLast(shown);
    return shown;
}
